<template>
	<div>
		<v-snackbar class="noPrint" v-model="snackbar.show" top :color="snackbar.color" :timeout="snackbar.timeout">
			{{ snackbar.message }}
			<v-btn :color="snackbar.buttonColor" flat @click="snackbar.show = false"> Close </v-btn>
		</v-snackbar>

		<v-alert v-if="position && showEmployeeAcknowledgeDialog" :value="true" class="text-xs-left mb-4" icon="mdi-alert-circle-outline" color="warning">
			It is required that you acknowledge the contents of this position description. <a style="color: #fff;text-decoration: underline;" @click.stop="scrollToAcknowledgement">This can be done at the bottom of the document</a>
		</v-alert>

		<v-card flat v-if="position">
			<v-toolbar card prominent :extension-height="42">
				<router-link class="mt-3" :to="{ name: 'employee', params: { id: employeeId } }">
					<v-avatar style="margin-top:3px; margin-bottom:3px;" :size="40" color="lighten-4" class="mr-3">
						<img :src="getAvatar(employeeId)" alt="avatar" class="avatar-img" />
					</v-avatar>
				</router-link>
				
				<div class="text-xs-left mt-3 mr-2">
					<h4 class="headline" style="margin-bottom: -1px;">
						{{ position.title }}
						<v-tooltip v-if="isApproved(position)" bottom>
						
							<v-chip slot="activator" 
								small 
								:outline="getStatusChipOutline(position.status)" 
								:color="getStatusChipColor(position.status)" 
								:text-color="getStatusChipTextColor(position.status)">{{ `PD - ${getStatusFriendly(position.status)}` }}
							</v-chip>				

							<span>{{ approvedText }}</span>
						</v-tooltip>

						<v-chip v-else
								small 
								:outline="getStatusChipOutline(position.status)" 
								:color="getStatusChipColor(position.status)" 
								:text-color="getStatusChipTextColor(position.status)">{{ `PD - ${getStatusFriendly(position.status)}` }}
							</v-chip>	
					</h4>
					<router-link class="employee-name" style="margin-left: 2px;" :to="{ name: 'employee', params: { id: employeeId } }">
						{{ employeeName }}
					</router-link>
					<div v-if="position && position.reportsTo" style="margin-left: 2px;" class="reports-to-text">
						<v-icon small class="mr-1 ml-2">mdi-file-tree</v-icon>
						<span class="mr-1">Reports to</span>
						<router-link :to="{ name: 'employee', params: { id: position.reportsTo.id } }">{{ position.reportsTo.fullName }}
						</router-link>
					</div>
					
				</div>		

				<v-spacer></v-spacer>
			
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="showSendEmailDialog = true"><v-icon dark>mail</v-icon></v-btn>
					</template>
					<span class="noPrint">Email Position Description</span>
				</v-tooltip>
				
				<v-tooltip bottom>
					<template v-slot:activator="{ on }">
						<v-btn icon v-on="on" @click="printClick">
							<v-icon>print</v-icon>
						</v-btn>
					</template>
					<span class="noPrint">Print Position Description</span>
				</v-tooltip>

				<v-tooltip bottom v-if="canEdit">
					<template v-slot:activator="{ on }">
						<v-btn v-on="on" 
						icon 
						@click="onEditClick"
						><v-icon dark>edit</v-icon> </v-btn>
					</template>
					<span class="noPrint">Edit Position Description</span>
				</v-tooltip>

				<v-btn small color="primary" v-if="showApproveButton" @click="showApproveDialog = true">Approve </v-btn>

				<v-menu v-if="canEdit">
					<template #activator="{ on: menu }">
						<v-tooltip bottom>
						<template #activator="{ on: tooltip }">
							<v-icon v-on="{ ...tooltip, ...menu }" >
								more_vert
							</v-icon>
						</template>
						<span>More Options</span>
						</v-tooltip>
					</template>
					<v-list>
						<v-list-tile small @click="showConfirmDeleteDialog = true">
							<v-list-tile-title>Delete Position Description</v-list-tile-title>
						</v-list-tile>
						<v-list-tile v-if="loggedInEmployee.isAdmin" small @click="showConvertToTemplateDialog = true">
							<v-list-tile-title>Convert Position to Template</v-list-tile-title>
						</v-list-tile>
					</v-list>
				</v-menu>

				<template v-slot:extension>
					<v-spacer></v-spacer>
					<div class="d-block pd-signatures">
						<div class="info-text">
							<span v-if="isApproved(position)">
								<v-icon color="green" small class="mr-1">mdi-check-circle-outline</v-icon>{{ approvedText }}
							</span>
							<span v-else>
								<v-icon color="error" small class="mr-1">mdi-alert-circle-outline</v-icon>This position description has not yet been approved
							</span>
						</div>
						<div class="info-text" v-if="isApproved(position)">
							<span v-if="isAcknowledged(position) && !isAcknowledgedBySystemAdmin(position)">
								<v-icon color="green" small class="mr-1">mdi-check-circle-outline</v-icon>{{ acknowledgedText }}
							</span>
							<span v-else-if="isApprovedPendingAcknowledgement(position)">
								<v-icon color="error" small class="mr-1">mdi-alert-circle-outline</v-icon>
								PD awaiting employee acknowledgement <span v-if="!isLoggedInEmployee">| <a style="font-style: normal;" @click="showSendEmailToEmployeeDialog = true">Send to employee for acknowledgement</a></span>
							</span>
							<span v-else-if="isLive(position) && !isAcknowledged(position)">
								<v-icon color="green" small class="mr-1">mdi-check-circle-outline</v-icon>Employee acknowledgement not required
							</span>
						</div>
					</div>
				</template>
			</v-toolbar>
			<v-card-text>
				<div class="header printOnly">
					<v-layout row wrap>
						<v-flex md3 class="text-xs-left">
							<div class="logo">
								<img :src="loggedInTenant.logoUrl" />
							</div>
						</v-flex>

						<v-flex md6 class="mt-3" style="text-align: center;">
							<div style="margin-bottom: 5px;" class="info-text">Position Description</div>
							<h1>{{ employeeName }}</h1>						
							<div>{{ position.title }}</div>
							<div v-if="position && position.reportsTo" class="info-text">Reports to {{ position.reportsTo.fullName }}</div>
						</v-flex>

						<v-flex md3 class="text-xs-left mt-3">
							<div class="header-right info-text">
								<div v-if="isApproved(position)">Approved by {{ position.approval.approvedBy.fullName }}, {{ position.approval.approvalDateUtc | formatDate }}</div>
								<div v-if="isAcknowledged(position) && !isAcknowledgedBySystemAdmin(position)">Acknowledged by {{ getAcknowledgedBy(position, employeeName) }}, {{ position.employeeAcknowledgement.acknowledgementDateUtc | formatDate }}</div>
								<div v-else-if="isLive(position) && !isAcknowledged(position)">Employee acknowledgement not required</div>
								<div v-if="!isApproved(position)">Not yet approved</div>
							</div>
						</v-flex>
					</v-layout>
				</div>

				<div class="content">
					<v-layout row wrap>
						<v-flex text-xs-left md8 sm12>
							<div class="text-xs-left">
								<h2 style="margin-bottom: 20px; ">
									Position Purpose
								</h2>
							</div>

							<p style="font-size:13px; margin-bottom: 20px;">{{ position.description }}</p>
						</v-flex>
					</v-layout>

					<v-divider style="margin-bottom:20px;"></v-divider>

					<div v-for="(type, index) in pdItemsSorted" :key="index">
						<div class="text-xs-left">
							<h2 style="margin-top: 20px; margin-bottom: 10px;">
								{{ type.typeFriendly }}
							</h2>
						</div>

						<div v-for="t in type.items" :key="t.id">
							<div class="text-xs-left">
								<h4 v-if="t.category !== 'N/A'" style="margin-top:30px; margin-bottom:20px; color: rgba(0,0,0,0.54);">
									{{ t.category }}
								</h4>
							</div>
							<div v-for="s in t.items" :key="s.id" class="item">
								<v-layout row wrap style="margin-bottom: 5px;">
									<v-flex text-xs-left md8 sm12>
										<div style="font-size:13px;">
											<div
												style="border-left-color: lightgray;
                    					padding-left: 10px; padding-right: 10px;"
												:class="{ 'item-text': true }"
											>
												<table>
													<tr>
														<td>
															<v-btn :class="{ 'item-btn': true, disabled: true }" fab small>
																<v-icon style="font-size: 14px;">keyboard_arrow_right</v-icon>
															</v-btn>
														</td>
														<td>{{ s.item }}</td>
													</tr>
												</table>
											</div>

											<div v-for="outcome in s.outcomes" :key="outcome.id" style="margin-left:20px;">
												<div style="border-left-color: lightgray; padding-left: 10px; padding-right: 10px;" :class="{ 'item-text': true }">
													<table>
														<tr>
															<td>
																<v-btn :class="{ 'item-btn': true, disabled: true }" fab small>
																	<v-icon style="font-size: 14px;">keyboard_arrow_right</v-icon>
																</v-btn>
															</td>
															<td>{{ outcome.outcome }}</td>
														</tr>
													</table>
												</div>
											</div>
										</div>
									</v-flex>
								</v-layout>
							</div>
						</div>

						<v-divider style="margin-bottom:20px;"></v-divider>
					</div>
				</div>
			</v-card-text>
		</v-card>

		<v-layout row class="noPrint">
			<v-flex lg6>
				<acknowledge-position-description-dialog 
				id="pd-acknowledgement"
				v-if="showEmployeeAcknowledgeDialog" 
				class="mt-4"
				:isAcknowledging="isAcknowledging"
				@onAcknowledge="onAcknowledge" />
			</v-flex>
		</v-layout>

		<approve-position-description-dialog 
			:dialog="showApproveDialog"
			:isApproving="isApproving"
			:requireEmployeeAcknowledgement="requireEmployeeAcknowledgement"
			@onApprove="onApprove"
			@onCancel="showApproveDialog = false" />


		<employee-position-editor
			v-if="showEditor"
			:dialog="showEditor"
			title="Edit Position Description"
			:position="position"
			:position-templates="positionTemplates"
			:employees="employees"
			:employee-id="employeeId"
			:is-admin="loggedInEmployee.isAdmin"
			:is-saving="isSavingPosition"
			:requireEmployeeAcknowledgement="requireEmployeeAcknowledgement"
			@save="savePosition"
			@closeEditor="showEditor = false"
			@convertPositionToTemplate="convertPositionToTemplate"
            ></employee-position-editor>

		<confirmation-dialog v-if="showConfirmDeleteDialog"
			:dialog="showConfirmDeleteDialog"
			text="Are you sure you want to delete this position description?"
			:isConfirming="isDeleting"
			@onConfirm="onDelete"
			@onCancel="showConfirmDeleteDialog = false" />

		<loading-indicator 
				v-if="isLoading" 
				loading-text="Loading Position Description..." />

		<send-email-dialog
			v-if="showSendEmailDialog"
			:dialog="showSendEmailDialog"
			title="Email Position Description"
			:subject="$_getEmailMeta(loggedInEmployee, employee, position).subject"
			:body="$_getEmailMeta(loggedInEmployee, employee, position).body"
			@onCancel="showSendEmailDialog = false"
			@onSent="onPositionDescriptionSent"
			>
			<template v-slot:below-body>
				<div class="info-text" style="font-style: normal;">
					<span class="mr-1">Position Description link</span><v-icon small>link</v-icon> <br><a :href="$_getPositionDescriptionLink(employee,position)" target="_blank">{{ $_getPositionDescriptionLink(employee,position)}}</a>
				</div>
			</template>
		</send-email-dialog>

		<send-email-dialog
			v-if="showSendEmailToEmployeeDialog"
			:dialog="showSendEmailToEmployeeDialog"
			title="Send to Employee"
			:recipients="[$_mapEmployeeToEmailRecipientModel(employee)]"
			:subject="$_getEmailMetaAfterPositionApproval(loggedInEmployee, employee, position, requireEmployeeAcknowledgement).subject"
			:body="$_getEmailMetaAfterPositionApproval(loggedInEmployee, employee, position, requireEmployeeAcknowledgement).body"
			@onCancel="showSendEmailToEmployeeDialog = false"
			@onSent="onPositionDescriptionSentToEmployee"
			>
			<template v-slot:header>
				<div v-if="positionDescriptionJustApproved">
					<v-alert class="text-xs-left" :value="true" outline type="success">{{ positionApprovedMessage }} Would you like to send a copy to the employee?</v-alert>
					<div class="text-xs-right mb-4" style="margin-top: -3px;"><a @click="showSendEmailToEmployeeDialog = false">Cancel</a></div>
				</div>	
			</template>
			<template v-slot:below-body>
				<div class="info-text" style="font-style: normal;">
					<span class="mr-1">Position Description link</span><v-icon small>link</v-icon> <br><a :href="$_getPositionDescriptionLink(employee,position)" target="_blank">{{ $_getPositionDescriptionLink(employee,position)}}</a>
				</div>
			</template>
		</send-email-dialog>

		<confirmation-dialog v-if="showConfirmEditDialog"
			:dialog="showConfirmEditDialog"
			:text="confirmEditDialogText"
			title="Information"
			confirmButtonText="Proceed"
			@onConfirm="editPosition"
			@onCancel="showConfirmEditDialog = false" />

			<ConvertPositionToTemplateDialog v-if="showConvertToTemplateDialog" :dialog="showConvertToTemplateDialog" :position="position" @closeDialog="showConvertToTemplateDialog = false" />

	</div>
</template>

<script>
	import ApprovePositionDescriptionDialog from '@/components/employee/ApprovePositionDescriptionDialog';
	import AcknowledgePositionDescriptionDialog from '@/components/employee/AcknowledgePositionDescriptionDialog';
	import EmployeePositionEditor from '@/components/employee/EmployeePositionEditor';
	import typeFriendlyMixin from '@/mixins/typeFriendlyMixin';
	import positionStatusMixin from '@/mixins/positionStatusMixin';
	import avatarMixin from '@/mixins/avatarMixin';
	import emailMixin from '@/mixins/emailMixin';
	import positionDescriptionEmailMixin from '@/mixins/positionDescriptionEmailMixin';
	import LoadingIndicator from '@/components/common/LoadingIndicator';
	import SendEmailDialog from '@/components/email/SendEmailDialog';
	import PositionStatus from '@/enums/positionStatus';
	import { mapGetters } from 'vuex';
	import ConvertPositionToTemplateDialog from '@/components/employee/ConvertPositionToTemplateDialog';

	export default {
		components: {
			ApprovePositionDescriptionDialog,
			AcknowledgePositionDescriptionDialog,
			EmployeePositionEditor,
			LoadingIndicator,
			SendEmailDialog,
			ConvertPositionToTemplateDialog
		},
		props: {
			employeeId: {
				required: true,
				type: String
			},
			id: {
				required: true,
				type: String
			}
		},
		mixins: [typeFriendlyMixin, positionStatusMixin, avatarMixin, emailMixin, positionDescriptionEmailMixin],
		data() {
			return {
				showApproveDialog: false,
				snackbar: {
					show: false,
					message: '',
					color: undefined,
					buttonColor: 'pink',
					timeout: 6000
				},
				position: null,
				isLoading: false,
				isAcknowledging: false,
				isApproving: false,
				notFound: false,
				showEditor: false,
				isSavingPosition: false,
				showConfirmDeleteDialog: false,
				isDeleting: false,
				showSendEmailDialog: false,
				showSendEmailToEmployeeDialog: false,
				showConfirmEditDialog: false,
				positionDescriptionJustApproved: false,
				showConvertToTemplateDialog: false
			};
		},
		watch: {
			showSendEmailToEmployeeDialog(newVal, oldVal) {
				if (oldVal)
					this.positionDescriptionJustApproved = false;
			}
		},
		created() {
			this.isLoading = true;

			this.$store.dispatch('employees/getEmployee', this.employeeId).then(
				employee => {
					this.isLoading = false;
				
					const foundPosition = employee.positions
						.find(x => x.id === this.id);

					if (!foundPosition) {
						this.notFound = true;
						return;
					}

					this.position = foundPosition;

					this.$store.dispatch('setPageTitle', "");
				},
				error => {
					this.isLoading = false;
					this.showSnackbar('An error occurred loading position description', true);
				}
			);
		},
		computed: {
			...mapGetters({
				loggedInEmployee: 'loggedInEmployee',
				loggedInTenant: 'loggedInTenant',
				positionTemplates: 'positionTemplates/positionTemplateList',
				employees: 'employees/employeesMiniActiveOnly'
			}),
			employee() {
				return this.$store.getters['employees/getEmployeeMini'](this.employeeId);
			},
			employeeName() {
				return this.employee != null ? this.employee.fullName : "";
			},
			pdItemsSorted() {
				// group by type
				let groupedByType = _.groupBy(this.position.positionItems, 'type');
				let result = [];

				for (let type in groupedByType) {
					result.push({
						type: type,
						typeFriendly: this.getTypeFriendly(type, true),
						items: []
					});

					// group by category
					let groupedByCategory = _.groupBy(groupedByType[type], 'category');

					for (let category in groupedByCategory) {
						result[result.length - 1].items.push({
							category: category,
							items: groupedByCategory[category]
						});
					}
				}

				let sortOrder = [1, 2, 5, 0, 3, 4];
				let resultsOrdered = [];
				for (let i = 0; i < sortOrder.length; i++) {
					for (let x = 0; x < result.length; x++) {
						if (result[x].type == sortOrder[i]) {
							resultsOrdered.push(result[x]);
						}
					}
				}

				return resultsOrdered;
			},
			showApproveButton() {
				if (this.position.status !== PositionStatus.Draft) {
					return false;
				}

				// Only managers & admins can approve
				if (this.loggedInEmployee.isAdmin) {
					return true;
				}

				// Employees cannot approve their own
				if (this.loggedInEmployee.id === this.employeeId) {
					return false;
				}

				if (this.loggedInEmployee.underlings != null) {
					return this.loggedInEmployee.underlings.includes(this.employeeId);
				}
			},
			showEmployeeAcknowledgementWarning() {
				if (!this.position) return false;

				return this.isApprovedPendingAcknowledgement(this.position) && this.loggedInEmployee.id !== this.employeeId;
			},
			showEmployeeAcknowledgeDialog() {
				if (!this.position) return false;

				return this.isApprovedPendingAcknowledgement(this.position) && this.loggedInEmployee.id === this.employeeId;
			},
			requireEmployeeAcknowledgement() {
				return !(
					this.loggedInTenant &&
					this.loggedInTenant.tenantSettings &&
					this.loggedInTenant.tenantSettings.requireEmployeeAcknowledgementForPositionDescriptions === false
				);
			},
			positionApprovedMessage() {
				return this.requireEmployeeAcknowledgement
					? 'Position Description Approved.'
					: 'Position Description Approved and now Live.';
			},
			approvedText() {
				if (!this.isApproved(this.position))
					return '';

				const approvedBy = this.position.approval.approvedBy.fullName;
				const approvedDate = this.$options.filters.formatDateTime(this.position.approval.approvalDateUtc);
				
				return `Approved by ${approvedBy} on ${approvedDate}`;
			},
			acknowledgedText() {
				if (!this.isAcknowledged(this.position))
					return '';

				const acknowledgedDate = this.$options.filters.formatDateTime(this.position.employeeAcknowledgement.acknowledgementDateUtc);
				
				return `Acknowledged by ${this.getAcknowledgedBy(this.position, this.employeeName)} on ${acknowledgedDate}`;
			},
			confirmEditDialogText() {
				if (!this.position) return null;

				if (this.isLive(this.position)) {
					return `This position is already live. A new draft will be created for any changes that are saved.`;
				} else if (this.isApproved(this.position)) {
					return `This position has already been approved by <strong>${this.position.approval.approvedBy.fullName}</strong>. Any changes made to the position will reset the status back to <strong>Draft</strong>.`;
				}

				return null;
			},
			canEdit() {
				const isUnderling = this.loggedInEmployee.underlings != null && this.loggedInEmployee.underlings.includes(this.employeeId);
				return this.loggedInEmployee.isAdmin || isUnderling;
			},
			isLoggedInEmployee() {
				return this.loggedInEmployee.id === this.employeeId;
			}
		},
		methods: {
			convertToTemplateClick() {
				this.showConvertToTemplateDialog = true;
			},
			onApprove() {
				this.isApproving = true;
				this.$store.dispatch('employees/approvePosition', { employeeId: this.employeeId, positionId: this.position.id }).then(
					updatedEmployee => {
						this.showSnackbar(this.positionApprovedMessage);
						this.isApproving = false;
						this.showApproveDialog = false;

						const foundPosition = updatedEmployee.positions
							.find(x => x.id === this.id);

						this.position = foundPosition;
						this.positionDescriptionJustApproved = true;
						this.showSendEmailToEmployeeDialog = true;

					},
					error => {
						this.showApproveDialog = false;
						this.isApproving = false;
						this.showSnackbar("The Position Description could not be approved at this time", true);
					}
				);
			},
			onAcknowledge() {
				this.isAcknowledging = true;
				this.$store.dispatch('employees/acknowledgePosition', { employeeId: this.employeeId, positionId: this.position.id }).then(
					updatedEmployee => {
						this.showSnackbar("Position Description has been acknowledged, it is now Live");
						this.isAcknowledging = false;

						const foundPosition = updatedEmployee.positions
							.find(x => x.id === this.id);

						this.position = foundPosition;

						this.$emit("onAcknowledged");

					},
					error => {
						this.isAcknowledging = false;
						this.showSnackbar("The Position Description could not be acknowledged at this time", true);
					}
				);
			},
			printClick() {
				window.print();
			},
			showSnackbar(message, isError) {
				this.snackbar.show = true;
				this.snackbar.message = message;
				this.snackbar.color = isError ? 'error' : undefined;
				this.snackbar.buttonColor = isError ? '#fff' : 'pink';
				this.snackbar.timeout = isError ? 0 : 6000;
			},
			savePosition(position) {
				this.isSavingPosition = true;
				const newDraftToBeCreated = position.triggerApprovalWorkflow && this.isLive(this.position);

				this.$store.dispatch('employees/updatePosition', {
					employeeId: this.employeeId,
					position
				}).then(
				updatedEmployee => {
					this.isSavingPosition = false;

					if (newDraftToBeCreated) {
						this.$router.push({ name: 'employee', params: { id: this.employeeId, previousAction: "new_position_draft_created" } });
					} else {
						const foundPosition = updatedEmployee.positions
						.find(x => x.id === this.id);

						this.position = foundPosition;
				
						this.showSnackbar("Position Updated");
						this.showEditor = false;
					}
				},
				error => {
					this.isSavingPosition = false;
					this.showEditor = false;
					this.showSnackbar("The Position Description could not be updated at this time", true);
				})
			},
			convertPositionToTemplate(template) {
				this.$store.dispatch('positionTemplates/add', template).then(
					response => {
						this.showSnackbar("Position Template Created");
					},
					error => {
						this.showSnackbar("The Position Template could not be created at this time");
					}
				);
			},
			onDelete() {
				this.isDeleting = true;
				this.$store.dispatch('employees/deletePosition', { employeeId: this.employeeId, positionId: this.id }).then(
					response => {
						this.isDeleting = false;
						this.$router.push({ name: 'employee', params: { id: this.employeeId } });
					},
					error => {
						this.isDeleting = false;
						this.showSnackbar("The Position could not be deleted at this time", true);
					}
				);
			},
			onPositionDescriptionSent() {
				this.showSendEmailDialog = false;
				this.showSnackbar("Position Description sent");
			},
			onPositionDescriptionSentToEmployee() {
				this.showSendEmailToEmployeeDialog = false;
				this.showSnackbar("Position Description sent to Employee");
			},
			onEditClick() {
				this.editPosition();
			},
			editPosition() {
				this.showEditor = true;
				this.showConfirmEditDialog = false;
			},
			scrollToAcknowledgement() {
				const element = document.querySelector("#pd-acknowledgement");

				// smooth scroll to element and align it at the bottom
				element.scrollIntoView({ behavior: 'smooth', block: 'end'});
			},
		}
	};
</script>

<style scoped>
	.header {
		min-height: 100px;
		margin-bottom: 25px;
	}
	.header .header-right {
		text-align: right;
	}
	.header h1 {
		font-size: 1.7em;
	}
	.content {
		text-align: left;
	}
	.info-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
		font-style: italic;
	}
	.logo img {
		max-height: 100px;
		max-width: 220px;
	}
	.pd-signatures {
		margin-top: -15px;
		text-align: right;
	}
	.reports-to-text {
		color: rgba(0, 0, 0, 0.54);
		font-size: 12px;
	}
	.v-card >>> .v-toolbar__content {
		height: 74px !important;
	}
</style>

<style type="text/css" media="print">
	@page {
		size: auto;
		margin: 10mm 5mm 10mm 5mm;
	}

</style>
